import { useMemo } from "react";
import type { ChangeEvent } from "react";
import type {
  FormAppearanceKey,
  FormAppearanceOverrides,
  FormThemesCatalog,
} from "../../../types/appearance";
import type { FormBuilderDraft } from "../../../types/formBuilder";
import { uploadDisplayImage } from "../../../api/upload";
import {
  APPEARANCE_KEY_LABELS,
  APPEARANCE_KEYS,
  isColorAppearanceKey,
  mergeAppearance,
} from "../../../lib/forms/appearance";
import { getPublicMediaUrl } from "../../../lib/media/getPublicMediaUrl";
import { FieldLabel, SelectInput, TextInput } from "../_components/AdminShell";

type AppearanceEditorProps = {
  draft: FormBuilderDraft;
  catalog: FormThemesCatalog;
  onChange: (patch: Partial<FormBuilderDraft>) => void;
};

function ColorField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  const colorValue = /^#[0-9a-f]{6}$/i.test(value) ? value : "#000000";

  return (
    <div>
      <FieldLabel size="compact">{label}</FieldLabel>
      <div className="mt-1 flex items-center gap-2">
        <input
          type="color"
          value={colorValue}
          onChange={(event) => onChange(event.target.value)}
          className="h-8 w-10 cursor-pointer rounded border border-slate-200 bg-white p-0.5"
          aria-label={label}
        />
        <TextInput
          size="compact"
          value={value}
          onChange={onChange}
          placeholder="#ffffff"
          className="min-w-0 flex-1 font-mono"
        />
      </div>
    </div>
  );
}

export function AppearanceEditor({
  draft,
  catalog,
  onChange,
}: AppearanceEditorProps) {
  const resolved = useMemo(
    () =>
      mergeAppearance(
        draft.appearanceTheme,
        draft.appearanceOverrides,
        catalog.themes,
      ),
    [draft.appearanceTheme, draft.appearanceOverrides, catalog.themes],
  );

  const setOverride = (key: FormAppearanceKey, value: string) => {
    const next: FormAppearanceOverrides = { ...draft.appearanceOverrides };
    const base =
      catalog.themes.find((theme) => theme.id === draft.appearanceTheme)
        ?.styles[key] ?? "";

    if (value.trim() === "" || value.trim() === base) {
      delete next[key];
    } else {
      next[key] = value.trim();
    }

    onChange({
      appearanceOverrides: next,
      appearanceCleared: false,
    });
  };

  const handleThemeChange = (themeId: string) => {
    onChange({
      appearanceTheme: themeId,
      appearanceOverrides: {},
      appearanceCleared: false,
    });
  };

  const handleBackgroundUpload = async (
    event: ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    try {
      const imageUrl = await uploadDisplayImage(file);
      setOverride("backgroundImage", imageUrl);
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Не удалось загрузить картинку";
      window.alert(message);
    }
  };

  return (
    <div className="space-y-5">
      <section className="rounded-xl border border-slate-200 bg-white p-4 shadow-card">
        <h2 className="font-onest text-sm font-semibold text-ink">Тема</h2>
        <p className="mt-1 font-onest text-xs text-slate-500">
          Выберите пресет и при необходимости переопределите отдельные
          параметры.
        </p>
        <div className="mt-3 max-w-sm">
          <SelectInput
            size="compact"
            value={draft.appearanceTheme}
            onChange={handleThemeChange}
          >
            {catalog.themes.map((theme) => (
              <option key={theme.id} value={theme.id}>
                {theme.label}
              </option>
            ))}
          </SelectInput>
        </div>
      </section>

      <section className="rounded-xl border border-slate-200 bg-white p-4 shadow-card">
        <h2 className="font-onest text-sm font-semibold text-ink">
          Тонкая настройка
        </h2>
        <div className="mt-3 grid gap-4 sm:grid-cols-2">
          {APPEARANCE_KEYS.map((key) => {
            if (key === "backgroundImage") {
              return (
                <div key={key} className="sm:col-span-2">
                  <FieldLabel size="compact">
                    {APPEARANCE_KEY_LABELS.backgroundImage}
                  </FieldLabel>
                  {resolved.backgroundImage ? (
                    <div className="mb-2 mt-1">
                      <img
                        src={getPublicMediaUrl(resolved.backgroundImage)}
                        alt=""
                        className="max-h-32 max-w-full rounded border border-slate-200 object-cover"
                      />
                    </div>
                  ) : null}
                  <div className="flex flex-wrap items-center gap-2">
                    <label className="cursor-pointer rounded border border-slate-200 bg-slate-50 px-2.5 py-1 font-onest text-xs text-slate-700 hover:bg-slate-100">
                      Загрузить
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={handleBackgroundUpload}
                      />
                    </label>
                    <TextInput
                      size="compact"
                      value={
                        draft.appearanceOverrides.backgroundImage ??
                        resolved.backgroundImage
                      }
                      placeholder="https://..."
                      onChange={(value) =>
                        setOverride("backgroundImage", value)
                      }
                      className="min-w-0 flex-1 font-mono"
                    />
                    {resolved.backgroundImage ? (
                      <button
                        type="button"
                        className="rounded px-2 py-1 font-onest text-xs text-red-600 hover:bg-red-50"
                        onClick={() => setOverride("backgroundImage", "")}
                      >
                        Удалить
                      </button>
                    ) : null}
                  </div>
                </div>
              );
            }

            const value = draft.appearanceOverrides[key] ?? resolved[key] ?? "";

            if (isColorAppearanceKey(key)) {
              return (
                <ColorField
                  key={key}
                  label={APPEARANCE_KEY_LABELS[key]}
                  value={value}
                  onChange={(next) => setOverride(key, next)}
                />
              );
            }

            return (
              <div key={key}>
                <FieldLabel size="compact">
                  {APPEARANCE_KEY_LABELS[key]}
                </FieldLabel>
                <TextInput
                  size="compact"
                  value={value}
                  onChange={(next) => setOverride(key, next)}
                  className="mt-1 font-mono"
                />
              </div>
            );
          })}
        </div>
      </section>

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 font-onest text-xs text-slate-700 hover:bg-slate-50"
          onClick={() =>
            onChange({
              appearanceTheme: catalog.defaultTheme,
              appearanceOverrides: {},
              appearanceCleared: true,
            })
          }
        >
          Сбросить оформление
        </button>
      </div>
    </div>
  );
}
